import { FC, ReactNode, useLayoutEffect, useRef } from "react";

/**
 * Keeps a card in view while its column scrolls by (1NOW-11173): the "sticky" Booking overview.
 *
 * `position: sticky` does nothing in this app: #root is `overflow: hidden` (public/index.html), and turning
 * that off app-wide was tried and reverted (1NOW-10528, the sticky site header was not wanted). So the card
 * follows the scroll itself: it is moved down inside `bounds` (the nearest ancestor that matches it, the
 * row the card sits in) so its top stays `top` px under the top of the screen (its bottom, if it is taller
 * than the screen), and stops at the bottom of that row. On narrower screens, where the card sits below the page, it does not move.
 */
const FollowScroll: FC<{ bounds: string; top?: number; className?: string; children: ReactNode }> = ({
  bounds,
  top = 24,
  className,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.closest<HTMLElement>(bounds);
    if (!el || !box) return;
    let frame = 0;
    const place = () => {
      frame = 0;
      // Under lg the card is a block in the flow, below what it belongs to.
      if (window.innerWidth < 1024) {
        el.style.transform = "";
        return;
      }
      const boxRect = box.getBoundingClientRect();
      const height = el.offsetHeight;
      const room = boxRect.height - height;
      // Its top stays `top` under the top of the screen; a card too tall for that keeps its bottom on screen
      // instead (an open Price details list), so the total and the button never go out of sight.
      const wanted =
        height + top > window.innerHeight - top ? window.innerHeight - top - height - boxRect.top : top - boxRect.top;
      const y = Math.min(Math.max(wanted, 0), Math.max(room, 0));
      el.style.transform = y > 0 ? `translateY(${Math.round(y)}px)` : "";
    };
    const soon = () => {
      if (!frame) frame = window.requestAnimationFrame(place);
    };
    window.addEventListener("scroll", soon, { passive: true });
    window.addEventListener("resize", soon);
    const watch = typeof ResizeObserver !== "undefined" ? new ResizeObserver(soon) : null;
    watch?.observe(box);
    watch?.observe(el);
    place();
    return () => {
      window.removeEventListener("scroll", soon);
      window.removeEventListener("resize", soon);
      watch?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      el.style.transform = "";
    };
  }, [bounds, top]);

  return (
    <div ref={ref} className={className} data-testid="follow-scroll">
      {children}
    </div>
  );
};

export default FollowScroll;
